// src/styles/commonHooks.js
import { useEffect, useState, useRef } from 'react';
import { getThemeVars, getSamsungThemeVars } from './theme';

export const useMobile = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 768px)');
    setIsMobile(mediaQuery.matches);

    const handler = (e) => setIsMobile(e.matches);
    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }, []);

  return isMobile;
};

export const useScrollToTop = () => {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);
};

export const useThemeVars = (theme, isSamsungBrowser) =>
  isSamsungBrowser ? getSamsungThemeVars() : getThemeVars(theme);

// ─── CSS custom properties ────────────────────────────────────────────────────

const THEME_DURATION = 700;
const THEME_DURATION_CSS = `${THEME_DURATION}ms`;
const THEME_EASING = 'ease';

export const globalThemeTransitionStyles = `
  *, *::before, *::after {
    transition:
      color ${THEME_DURATION_CSS} ${THEME_EASING},
      background-color ${THEME_DURATION_CSS} ${THEME_EASING},
      background ${THEME_DURATION_CSS} ${THEME_EASING},
      border-color ${THEME_DURATION_CSS} ${THEME_EASING},
      box-shadow ${THEME_DURATION_CSS} ${THEME_EASING},
      fill ${THEME_DURATION_CSS} ${THEME_EASING},
      stroke ${THEME_DURATION_CSS} ${THEME_EASING};
  }

  .no-theme-transition,
  .no-theme-transition * {
    transition: none !important;
  }
`;

const VAR_MAP = {
  bg:        '--theme-bg',
  fg:        '--theme-fg',
  card:      '--theme-card',
  btnBg:     '--theme-btn-bg',
  btnFg:     '--theme-btn-fg',
  accent:    '--theme-accent',
  footerBg:  '--theme-footer-bg',
  smallText: '--theme-small-text',
  subtleBg:  '--theme-subtle-bg',
  cardHover: '--theme-card-hover',
  cardBg:    '--theme-card-bg',
};

const applyThemeToCssVars = (themeVars) => {
  const root = document.documentElement;
  Object.entries(VAR_MAP).forEach(([key, varName]) => {
    if (themeVars[key] !== undefined) {
      root.style.setProperty(varName, themeVars[key]);
    }
  });
};

export const useThemeCssVars = (theme, isSamsungBrowser) => {
  const themeVars = useThemeVars(theme, isSamsungBrowser);

  useEffect(() => {
    applyThemeToCssVars(themeVars);
  }, [theme, isSamsungBrowser]); // eslint-disable-line react-hooks/exhaustive-deps

  const isFirst = useRef(true);
  if (isFirst.current) {
    if (typeof document !== 'undefined') applyThemeToCssVars(themeVars);
    isFirst.current = false;
  }

  const cssVars = {};
  Object.entries(VAR_MAP).forEach(([key, varName]) => {
    cssVars[key] = `var(${varName})`;
  });

  return { ...themeVars, ...cssVars };
};


// ─── Плавный фон body ─────────────────────────────────────────────────────────
//
// Вместо перехвата inline-стилей через MutationObserver — переносим
// управление фоном целиком в CSS-переменную --theme-body-bg.
// App.js должен вызвать setBodyBg(value) вместо прямой записи в body.style.
// CSS на body: background: var(--theme-body-bg); transition: background 700ms ease;
// Transition подхватывается браузером нативно — никаких оверлеев и таймеров.

const BODY_BG_VAR = '--theme-body-bg';

export const setBodyBg = (value) => {
  if (typeof document === 'undefined') return;
  document.documentElement.style.setProperty(BODY_BG_VAR, value);
};

export const useBodyBgTransition = () => {
  useEffect(() => {
    if (typeof document === 'undefined') return;

    const body = document.body;

    // Переносим текущий инлайн-фон (если был задан) в переменную,
    // затем убираем инлайн-стиль чтобы CSS-правило с var() вступило в силу.
    const current = body.style.background || body.style.backgroundColor || '';
    if (current) {
      document.documentElement.style.setProperty(BODY_BG_VAR, current);
      body.style.background = '';
      body.style.backgroundColor = '';
    }

    // Добавляем правило на body один раз через <style>, если его ещё нет.
    const STYLE_ID = '__theme-body-bg-style__';
    if (!document.getElementById(STYLE_ID)) {
      const style = document.createElement('style');
      style.id = STYLE_ID;
      style.textContent = `
        body {
          background: var(${BODY_BG_VAR}) !important;
          transition: background ${THEME_DURATION_CSS} ${THEME_EASING},
                      background-color ${THEME_DURATION_CSS} ${THEME_EASING};
        }
      `;
      document.head.appendChild(style);
    }
  }, []);
};